import React, { Fragment } from 'react'

import classNames from 'classnames'
import {
  ToggleCollectionContentAssociation,
  ToggleCollectionContentAssociationVariables,
} from 'globalTypes'
import { filter } from 'lodash'

import { useMutation, useQuery } from '@apollo/client'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCommentDots, faGrimace } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Trans } from '@lingui/macro'
import Paragraph from 'Root/App/Paragraph'
import Title from 'Root/App/Title'
import Loading from 'Root/Loader/Page'
import mixpanelTrack from 'lib/mixpanel-track'
import map from 'lodash/map'

import QUERY from './current-user-contents.graphql'
import MUTATION from './toggle-collection-content-association.graphql'
import MODULE_MUTATION from './toggle-collection-module-content-association.graphql'

// ----------------------------------------------------------------------------

library.add(faCommentDots, faGrimace)

export interface Props {
  id: any
  moduleId: any
  totalUserContents?: Number
}

const NoContentsForCollectionHelper = () => (
  <div className="pt-12 sm:pt-16 px-6 lg:px-8">
    <div className="flex flex-col items-center justify-center mx-auto gap-3 text-center max-w-md sm:gap-4">
      <Title size="M">
        <Trans>Ancora niente qui!</Trans>
        <FontAwesomeIcon
          icon={['fas', 'grimace']}
          className="text-brand-main ml-3"
        />
      </Title>
      <Paragraph size="L">
        <Trans>
          Qui troverai tutti i contenuti creati da te. Dopo averli creati, li
          troverai qui per poterli associare alla raccolta/corso.
        </Trans>
      </Paragraph>
      <Paragraph size="L">
        <Trans>Per chiarimenti, siamo a tua disposizione tramite la chat</Trans>
        <FontAwesomeIcon
          icon={['fas', 'comment-dots']}
          className="text-brand-accent4 ml-2"
        />
      </Paragraph>
    </div>
  </div>
)

const AssociateContents: React.FC<Props> = ({
  id,
  moduleId,
  totalUserContents,
}: Props) => {
  const { data, loading } = useQuery(QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      includes: id,
      moduleIncludes: moduleId,
    },
  })
  const [mutate, { loading: isMutating }] = useMutation<
    ToggleCollectionContentAssociation,
    ToggleCollectionContentAssociationVariables
  >(MUTATION)

  const [associateContentToModule, { loading: isContentModuleMutating }] =
    useMutation(MODULE_MUTATION)

  if (loading) {
    return <Loading />
  }

  const contents = map(data?.me?.contents?.edges || [], 'node')

  const renderContents = filter(contents, (content) => !content.isInCollection)

  const onChange = (content) => async (event) => {
    const checked = event.target.checked

    if (checked) {
      mixpanelTrack('Aggiunta Contenuto', {
        id_corso: id,
        id_contenuto: content.id,
        tipo_contenuto: content.__typename,
      })
    }

    if (moduleId) {
      return await associateContentToModule({
        variables: {
          associated: checked,
          collectionId: id,
          moduleId: moduleId,
          contentId: content.id,
        },
      })
    }

    return await mutate({
      variables: {
        contentId: content.id,
        collectionId: id,
        associated: checked,
      },
    })
  }

  return (
    <Fragment>
      {totalUserContents === 0 && <NoContentsForCollectionHelper />}

      {renderContents.length > 0 && (
        <ul className="divide-y divide-gray-200">
          {map(renderContents, (content) => {
            return (
              <li key={content.id} className="p-4">
                <label
                  className={classNames(
                    {
                      'opacity-50': isMutating,
                    },
                    'inline-flex items-center cursor-pointer'
                  )}
                >
                  <input
                    className="cursor-pointer form-checkbox h-8 w-8 rounded-md border-gray-300 p-4"
                    defaultChecked={
                      content?.isInCollection || content?.isInModule
                    }
                    onChange={onChange(content)}
                    type="checkbox"
                    disabled={isMutating ?? isContentModuleMutating}
                  />
                  <span className="ml-4 text-xl">{content.title}</span>
                </label>
              </li>
            )
          })}
        </ul>
      )}
    </Fragment>
  )
}

export default AssociateContents
